import { useState } from 'react';
import { useTheme } from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';

import { CollapsiblePanel } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel';
import { CollapsiblePanelProvider } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel.context';
import { Label } from '../../../../../Toolkit/Inputs/Label';
import { FinanceTextField } from '../../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { FinanceSelect } from '../../../../financeProviders/shared/FinanceSelect';
import { Spacer } from '../../../../../../helpers/genericStyles';
import { Divider } from '../../../../../ToolkitV2/Divider/Divider';

import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS } from './constants';

import { type CloseBrothersForm } from '../../../../queries/schema/carFinance.schema';

const RESIDENTIAL_STATUS = [
  { label: 'Select status', value: '' },
  'Homeowner',
  'Tenant',
  'Living with Parents',
];
const COUNTIES = [
  { label: 'Select county', value: '' },
  'Carlow',
  'Cavan',
  'Clare',
  'Cork',
  'Donegal',
  'Dublin',
  'Galway',
  'Kerry',
  'Kildare',
  'Kilkenny',
  'Laois',
  'Leitrim',
  'Limerick',
  'Longford',
  'Louth',
  'Mayo',
  'Meath',
  'Monaghan',
  'Offaly',
  'Roscommon',
  'Sligo',
  'Tipperary',
  'Waterford',
  'Westmeath',
  'Wexford',
  'Wicklow',
];
const TIME_AT_ADDRESS_YEARS = [
  { value: '', label: 'Select time' },
  { value: '0', label: '0 years' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: '6 years' },
  { value: '7', label: '7 years' },
  { value: '8', label: '8 years' },
  { value: '9', label: '9 years' },
  { value: '10', label: '10+ years' },
];

const ResidentialDetails = () => {
  const [showPreviousAddressFields, setShowPreviousAddressFields] =
    useState(false);
  const { control, resetField } = useFormContext<CloseBrothersForm>();
  const theme = useTheme();

  const handleTimeAtCurrentAddressChange = (selected: string) => {
    if (
      Number(selected) >
      THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS
    ) {
      setShowPreviousAddressFields(false);
      resetField('previousAddress');
    } else {
      setShowPreviousAddressFields(true);
    }
  };

  return (
    <CollapsiblePanelProvider panelName="currentAddress">
      <CollapsiblePanel
        headerText="Residential Details"
        accordionName="Residential Details"
        mb={['S8', 'M16']}
        panelName="currentAddress"
      >
        <Spacer marginBottom={['M16', 'M24']} />
        <Label htmlFor="currentAddress.residentialStatus">
          Residential Status*
        </Label>
        <Controller
          control={control}
          name="currentAddress.residentialStatus"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={RESIDENTIAL_STATUS}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.addressLine1">
          Current Address Line 1*
        </Label>
        <Controller
          control={control}
          name="currentAddress.addressLine1"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter current address line 1"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.addressLine2">
          Current Address Line 2
        </Label>
        <Controller
          control={control}
          name="currentAddress.addressLine2"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter address line 2"
              isOptional
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.town">Town*</Label>
        <Controller
          control={control}
          name="currentAddress.town"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter town"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.county">County*</Label>
        <Controller
          control={control}
          name="currentAddress.county"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={COUNTIES}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.eircode">Eircode</Label>
        <Controller
          control={control}
          name="currentAddress.eircode"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter eircode"
              isOptional
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.timeAtCurrentAddress.years">
          Time at Current Address*
        </Label>
        <Controller
          control={control}
          name="currentAddress.timeAtCurrentAddress.years"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              onChange={(selected) => {
                field.onChange(selected);
                handleTimeAtCurrentAddressChange(selected);
              }}
              placeholderAsOption
              options={TIME_AT_ADDRESS_YEARS}
            />
          )}
        />
        {showPreviousAddressFields && (
          <>
            <div style={{ marginBottom: theme.spacing.S12 }}></div>
            <Divider />
            <div style={{ marginBottom: theme.spacing.M32 }}></div>
            <Label htmlFor="previousAddress.addressLine1">
              Previous Address Line 1*
            </Label>
            <Controller
              control={control}
              name="previousAddress.addressLine1"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter address line 1"
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.addressLine2">
              Previous Address Line 2
            </Label>
            <Controller
              control={control}
              name="previousAddress.addressLine2"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter address line 2"
                  isOptional
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.town">Previous Town*</Label>
            <Controller
              control={control}
              name="previousAddress.town"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter town"
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.county">Previous County*</Label>
            <Controller
              control={control}
              name="previousAddress.county"
              render={({ field, fieldState }) => (
                <FinanceSelect
                  {...field}
                  {...fieldState}
                  placeholderAsOption
                  options={COUNTIES}
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.eircode">Previous Eircode</Label>
            <Controller
              control={control}
              name="previousAddress.eircode"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter eircode"
                  isOptional
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.timeAtAddress.years">
              Time at Previous Address*
            </Label>
            <Controller
              control={control}
              name="previousAddress.timeAtAddress.years"
              render={({ field, fieldState }) => (
                <FinanceSelect
                  {...field}
                  {...fieldState}
                  placeholderAsOption
                  options={TIME_AT_ADDRESS_YEARS}
                />
              )}
            />
          </>
        )}
      </CollapsiblePanel>
    </CollapsiblePanelProvider>
  );
};

export { ResidentialDetails };
